import { branding } from '@fsd/shared';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { ArrowLeft, Menu, ShoppingCart, X } from 'lucide-react';
import logo from '@/assets/logo/logo.png';
import { useTranslation } from '@/i18n/hooks';
import { useCart } from '@/utils/useCart';
import { LanguageSwitcher } from '../languageSwitcher';
import { ThemeToggle } from '../themeToggle';
import { useHeader } from './useHeader';

export function Header() {
  const { t } = useTranslation('header');
  const { totalItems, setIsOpen } = useCart();
  const routerState = useRouterState();
  const navigate = useNavigate();
  const isCatalogPage = routerState.location.pathname === '/catalog';
  const isProductPage = routerState.location.pathname.startsWith('/product/');
  const { isMenuOpen, hasBackground, scrollToSection, toggleMenu, handleBackClick } = useHeader(
    navigate,
    isCatalogPage,
    isProductPage
  );

  const navigation = [
    { name: t('navigation.drones'), href: '#new-drones' },
    { name: t('navigation.buffalo'), href: '#buffalo-line' },
    { name: t('navigation.parts'), href: '#parts' },
    { name: t('navigation.about'), href: '#about' },
    { name: t('navigation.contact'), href: '#contact' },
  ];

  return (
    <header
      className={`fixed top-0 right-0 left-0 z-50 transition-all duration-500 ease-in-out ${
        hasBackground
          ? 'border-b border-slate-200/50 bg-white/80 shadow-lg backdrop-blur-lg dark:border-slate-700/50 dark:bg-slate-900/80'
          : 'border-b border-transparent bg-transparent'
      }`}
    >
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4 lg:h-20">
          <div className="flex shrink-0 items-center gap-4">
            {isCatalogPage || isProductPage ? (
              <>
                <button
                  type="button"
                  onClick={handleBackClick}
                  className="group flex items-center gap-2 rounded-lg px-3 py-2 text-slate-700 transition-colors duration-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                  aria-label={t('actions.back')}
                >
                  <ArrowLeft className="h-5 w-5" />
                  <span className="hidden sm:inline">{t('actions.back')}</span>
                </button>
                {isCatalogPage && (
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 lg:hidden">
                    {t('catalog.title')}
                  </h2>
                )}
              </>
            ) : (
              <button
                type="button"
                onClick={() => scrollToSection('#start')}
                aria-label={t('logo.ariaLabel')}
                className="group flex cursor-pointer items-center space-x-1 transition-all duration-500 hover:scale-105"
              >
                <div className="flex h-12 w-12 items-center justify-center overflow-hidden lg:h-14 lg:w-14">
                  <img
                    src={logo}
                    alt={branding.brandName}
                    className="h-full w-full object-contain"
                  />
                </div>
                <div className="hidden sm:block">
                  <h1 className="text-lg text-slate-900 transition-colors duration-500 dark:text-slate-100">
                    <span className="font-extrabold">{branding.brandNamePart1}</span>{' '}
                    <span className="font-normal">{branding.brandNamePart2}</span>
                  </h1>
                </div>
              </button>
            )}
          </div>

          {!isCatalogPage && !isProductPage && (
            <div className="hidden items-center space-x-8 lg:flex">
              {navigation.map((item) => (
                <button
                  type="button"
                  key={item.name}
                  onClick={() => scrollToSection(item.href)}
                  aria-label={`Ir para seção ${item.name}`}
                  className="group relative font-medium text-slate-700 transition-colors duration-500 hover:text-slate-900 dark:text-slate-300 dark:hover:text-slate-100"
                >
                  {item.name}
                  <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-primary transition-all duration-500 group-hover:w-full"></span>
                </button>
              ))}
            </div>
          )}

          {isCatalogPage && (
            <div className="hidden items-center lg:flex">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                {t('catalog.title')}
              </h2>
            </div>
          )}

          <div className="hidden items-center space-x-2 lg:flex">
            <LanguageSwitcher />
            <ThemeToggle />
            <button
              type="button"
              onClick={() => setIsOpen(true)}
              aria-label={t('actions.cart')}
              className="relative rounded-lg p-2 text-slate-600 transition-all duration-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
            >
              <ShoppingCart className="h-5 w-5" />
              {totalItems > 0 && (
                <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
                  {totalItems > 99 ? '99+' : totalItems}
                </span>
              )}
            </button>
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <LanguageSwitcher />
            <ThemeToggle />
            <button
              type="button"
              onClick={() => setIsOpen(true)}
              aria-label={t('actions.cart')}
              className="relative rounded-lg p-2 text-slate-600 transition-colors duration-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
            >
              <ShoppingCart className="h-5 w-5" />
              {totalItems > 0 && (
                <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
                  {totalItems > 99 ? '99+' : totalItems}
                </span>
              )}
            </button>
            {!isCatalogPage && !isProductPage && (
              <button
                type="button"
                onClick={toggleMenu}
                className="relative rounded-lg p-2 text-slate-600 transition-colors duration-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
                aria-label={t('mobileMenu.toggleAriaLabel')}
                aria-controls="mobile-menu"
                aria-expanded={isMenuOpen}
              >
                <Menu
                  className={`h-6 w-6 transition-transform duration-500 ease-in-out ${
                    isMenuOpen ? 'scale-0 rotate-90' : 'scale-100 rotate-0'
                  }`}
                />
                <X
                  className={`absolute top-2 left-2 h-6 w-6 transition-transform duration-500 ease-in-out ${
                    isMenuOpen ? 'scale-100 rotate-0' : 'scale-0 -rotate-90'
                  }`}
                />
              </button>
            )}
          </div>
        </div>

        {!isCatalogPage && !isProductPage && (
          <div
            id="mobile-menu"
            className={`grid transition-all duration-500 ease-in-out lg:hidden ${
              isMenuOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
            }`}
          >
            <div className="overflow-hidden">
              <div className="space-y-2 border-t border-slate-200 pt-2 pb-4 dark:border-slate-700">
                {navigation.map((item, index) => (
                  <button
                    type="button"
                    aria-label={`Ir para seção ${item.name}`}
                    key={item.name}
                    onClick={() => scrollToSection(item.href)}
                    className="block w-full rounded-lg px-4 py-3 text-left font-medium text-slate-700 transition-all duration-500 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                    style={{
                      transitionDelay: `${isMenuOpen ? index * 50 + 100 : 0}ms`,
                      opacity: isMenuOpen ? 1 : 0,
                      transform: isMenuOpen ? 'translateY(0)' : 'translateY(-10px)',
                    }}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
